import { useCallback, useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext.jsx";
import API from "../config/api.js";

function SocialAccountRow({ account, idField, nameField, imageField, fallbackImage, isExpired, expiredMessage, reconnectUrl, onSetActive, onRemove }) {
  const id    = account[idField];
  const name  = account[nameField] || "";
  const image = account[imageField] || "";

  const statusBadge = () => {
    if (isExpired)      return <small className="social-media-status rounded-4 text-white p-1 px-2 bg-danger">Expired</small>;
    if (account.active) return <small className="social-media-status rounded-4 text-white p-1 px-2 bg-primary">Active</small>;
    return <small className="social-media-status rounded-4 text-white p-1 px-2 bg-light text-dark"><i className="fi fi-rr-bullet" /> Offline</small>;
  };

  return (
    <div className="p-3 social-media">
      <div className="row g-2 align-items-center">
        <div className="col-3 col-md-2">
          <img src={image} onError={(e) => { e.target.src = fallbackImage; }} alt="" className="rounded-circle img-fluid" width="40" height="40" />
        </div>
        <div className="col-8 col-md-5 lh-1">
          <span>{name}</span>
        </div>
        <div className="col-6 col-md-5 d-flex justify-content-end align-items-center gap-2">
          {statusBadge()}
          <input style={{ borderRadius: 6 }} className="form-check-input social-media-action" type="radio"
            checked={!!account.active} onChange={() => onSetActive(id)} />
          <button type="button" className="btn p-1 social-media-action" onClick={() => onRemove(id)}>
            <i className="fi fi-rr-trash" />
          </button>
        </div>
      </div>
      {isExpired && (
        <>
          <p className="text-danger small mt-2 mb-1"><strong>{expiredMessage}</strong></p>
          <a href={reconnectUrl} className="btn btn-sm btn-secondary animate__animated animate__pulse animate__infinite">
            🚨 Reconnect Now
          </a>
        </>
      )}
    </div>
  );
}

function SocialColumn({ icon, label, connectUrl, connectLabel, accounts, children, connectButton }) {
  return (
    <div className="col-md-3">
      <div style={{ background: "#F7F5FD" }} className="rounded-3 p-3 fw-700 d-flex align-items-center justify-content-between">
        <span>
          <img src={`/assets/img/${icon}.png`} width="18" height="18" className="me-2 align-text-bottom" alt={label} />
          {label}
        </span>
        {connectButton ? connectButton : (
          <a href={connectUrl}>
            <img src="/assets/img/circle-plus2.png" width="26" height="26" className="align-text-bottom" alt="connect" />
          </a>
        )}
      </div>
      {accounts.length === 0 ? (
        <div className="text-muted text-center mt-4">
          <p>No accounts connected yet</p>
          <a href={connectUrl}>{connectLabel}</a>
        </div>
      ) : children}
    </div>
  );
}

const SOCIAL_MEDIA_CSS = `
  .social-media .social-media-action { display: none; }
  .social-media:hover .social-media-action { display: block; }
  .social-media:hover .social-media-status { display: none; }
  .btn-no-padding { padding: 0 !important; }
  .social-media .row{ min-height: 75px; }
`;

export default function AccountSocialMedia() {
  const { refreshAuth, user } = useAuth();
  const [social, setSocial]   = useState({ instagram: [], facebook: [], linkedin: [], tiktok: [] });
  const [loading, setLoading] = useState(true);

  const loadSocial = useCallback(async () => {
    setLoading(true);
    try {
      const res  = await fetch(`${API.account}?socialAccounts`, { credentials: "include" });
      const data = await res.json();
      setSocial(data);
    } catch (_) {}
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadSocial(); }, [loadSocial]);

  const socialAction = async (endpoint, body) => {
    const fd = new FormData();
    Object.entries(body).forEach(([k, v]) => fd.append(k, v));
    await fetch(`${API.account}?${endpoint}`, { method: "POST", credentials: "include", body: fd });
    loadSocial();
    refreshAuth?.();
  };

  const fbUrl = `${API.account}?connectFacebook`;
  const igUrl = `${API.account}?connectInstagram`;
  const liUrl = `${API.account}?connectLinkedin`;
  const ttUrl = `${API.account}?connectTiktok`;

  return (
    <div className="border rounded-4 p-4">
      <div className="row mb-3">
        <div className="col-md-9">
          <h2 className="fs-6 fw-700">Connected Social Media</h2>
          <p className="text-muted">Publish your content using the platform.</p>
        </div>
      </div>

      <style>{SOCIAL_MEDIA_CSS}</style>

      {loading ? (
        <div className="text-center py-3"><div className="spinner-border spinner-border-sm" /></div>
      ) : (
        <div className="row g-4">
          <SocialColumn
            icon="instagram" label="Instagram" connectUrl={igUrl} connectLabel="Connect Instagram Account"
            accounts={social.instagram}
            connectButton={
              <div className="dropdown d-inline-block">
                <button type="button" className="btn btn-no-padding p-0 border-0 bg-transparent" data-bs-toggle="dropdown" aria-expanded="false">
                  <img src="/assets/img/circle-plus2.png" width="26" height="26" className="align-text-bottom" alt="connect" />
                </button>
                <ul className="dropdown-menu">
                  <li><a className="dropdown-item" href={igUrl}>Connect via Instagram</a></li>
                  <li><a className="dropdown-item" href={fbUrl}>Connect via Facebook</a></li>
                </ul>
              </div>
            }>
            {social.instagram.map((acc) => (
              <SocialAccountRow key={acc.idusermeta} account={acc}
                idField="idusermeta" nameField="instagramusername" imageField="instagramprofilepicture"
                fallbackImage="/assets/img/profile-instagram.png"
                isExpired={acc.status === "expired"}
                expiredMessage="This Instagram connection has expired."
                reconnectUrl={igUrl}
                onSetActive={(id) => socialAction("setMetaActive", { idusermeta: id })}
                onRemove={(id) => socialAction("removeUserMeta", { idusermeta: id })}
              />
            ))}
          </SocialColumn>

          <SocialColumn icon="facebook" label="Facebook" connectUrl={fbUrl} connectLabel="Connect Facebook Account" accounts={social.facebook}>
            {social.facebook.map((acc) => (
              <SocialAccountRow key={acc.idusermeta} account={acc}
                idField="idusermeta" nameField="facebookpagename" imageField="facebookpageimage"
                fallbackImage="/assets/img/profile-facebook.png"
                isExpired={acc.status === "expired"}
                expiredMessage="This Facebook connection has expired."
                reconnectUrl={fbUrl}
                onSetActive={(id) => socialAction("setMetaActive", { idusermeta: id })}
                onRemove={(id) => socialAction("removeUserMeta", { idusermeta: id })}
              />
            ))}
          </SocialColumn>

          <SocialColumn icon="linkedin" label="LinkedIn" connectUrl={liUrl} connectLabel="Connect LinkedIn Account" accounts={social.linkedin}>
            {social.linkedin.map((acc) => (
              <SocialAccountRow key={acc.iduserlinkedin} account={acc}
                idField="iduserlinkedin" nameField="linkedinpagename" imageField="linkedinpageimage"
                fallbackImage="/assets/img/profile-linkedin.png"
                isExpired={acc.status === "expired"}
                expiredMessage="This LinkedIn connection has expired. Reconnect to continue posting."
                reconnectUrl={liUrl}
                onSetActive={(id) => socialAction("setLinkedinActive", { iduserlinkedin: id })}
                onRemove={(id) => socialAction("removeUserLinkedin", { iduserlinkedin: id })}
              />
            ))}
          </SocialColumn>

        
  <SocialColumn
    icon="tiktok"
    label="TikTok"
    connectUrl={ttUrl}
    connectLabel="Connect TikTok Account"
    accounts={social.tiktok}
  >
    {social.tiktok.map((acc) => (
      <SocialAccountRow
        key={acc.idusertiktok}
        account={acc}
        idField="idusertiktok"
        nameField="tiktokusername"
        imageField="tiktokuserimage"
        fallbackImage="/assets/img/profile-tiktok.png"
        isExpired={false}
        expiredMessage=""
        reconnectUrl={ttUrl}
        onSetActive={(id) => socialAction("setTiktokActive", { idusertiktok: id })}
        onRemove={(id) => socialAction("removeUserTiktok", { idusertiktok: id })}
      />
    ))}
  </SocialColumn>


        </div>
      )}
    </div>
  );
}